// Shared contextual answers for the C1 compiler and equip page. Consumers supply their held encounter, campaign, definitions and
// charges. Local environment gates use the same physical room/source readers as the scene (x/y floor, z elevation).
// null preserves a gate's no-context contract; unavailable held inputs throw so the caller reports an explicit unknown.
import { encounterLightTypesPresent } from '../../encounter-map/ft3d/light-presence';
import { admittedObscuredSources, boxAt, boxEnvironment, collectLightAndDarkSources, lightAt, lightContext,
    PERCEPTION_LIGHT, type PerceptionShape } from '../../encounter-map/ft3d/perception';
import { effectiveEncounterLightId } from '../../shared/env-types';
import { encounterCreatureField, encounterField, computedValue, sizeIdOf } from '../../shared/array-types';
import { combatCenter, combatSizeFt } from '../actions/ability-preconditions';
import { combatSizeCreditFt, combatVerticalSeparationFt } from '../../../../ft3d/engine/ft3d-combat-distance-core.js';
import { readInventoryField, readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { resolveFormulaVariables } from './formula-variable-resolution';
import { unmetEquippedItemRequirements, EquippedItemRequirementsUnknown } from './equipped-item-requirements';
import contract from './c1-contract.json';

type Row = Record<string, unknown>;
const isRow = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const U = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const scalar = (v: unknown): unknown => { let x = v; while (Array.isArray(x)) x = x[0]; return x; };
const list = (v: unknown): unknown[] => Array.isArray(v) ? v : isRow(v) ? Object.values(v) : [];

/** EnvironmentType::getId('underground' / 'underwater') — additions.php. */
export const ENVIRONMENT_UNDERGROUND = '38FB8933-47F6-4A79-60CF-14E80BCF9BF5';
export const ENVIRONMENT_UNDERWATER = '19BCE18F-E1DC-D359-48A0-3B789741E058';
/** SunState::getId('sun_state_day') — additions.php. */
export const SUN_STATE_DAY = '7650DB9F-465F-DDA6-9D7E-E064F4BC910F';

/** A context the page does not hold — the evaluator's "unknown" (the caller refuses / answers unknown with words). */
export class ConditionContextUnknown extends Error {
    constructor(readonly kind: string, readonly why: string) {
        super(why === 'no-scene' && ['present-light', 'effective-light', 'outdoors', 'sunlight', 'summon-within'].includes(kind)
            ? 'lightcontext31:condition-scene-not-held' : `unsupported-condition:${kind}:${why}`);
    }
}

export interface ConditionScene {
    /** The encounter the page holds (encounter_creatures_arr + encounter_inventories_arr as served); null = none held. */
    encounter: Row | null;
    /** The campaign row (campaign_light / campaign_sun_state); null / undefined = not held. */
    campaign: unknown;
    /** layer_value / layer_entries twins (the consumer's readers). */
    value: (row: Row | null, key: string) => unknown;
    entries: (row: Row | null, key: string) => unknown[];
    /** Static/library compilation supplies constructNpcWornEntries from its existing held definition context. */
    wornEquippedEntries?: (host: Row) => readonly unknown[];
    /** AbilityType::getAll() — the ability buckets the median-charge gate walks. */
    abilityTypes: readonly string[];
    equipmentSlots?: Readonly<Record<'main_hand' | 'off_hand' | 'both_hands', string>>;
    /** A full definition the page holds (an ability entry's ability_charges fallback), or null. */
    definition?: (id: string) => Row | null;
    /** query_charges(holder, item): the active row's count; null = none (the default applies); undefined = not held (unknown). */
    charges?: (holderId: string, itemId: string) => number | null | undefined;
}

/** The host's encounter id; '' when it has none (PHP's ft_encounter_context answers null → the gate is skipped). */
const hostEncounterId = (host: Row): string => {
    const id = U(scalar(isRow(host.encounter_creatures) ? host.encounter_creatures.encounter_id : undefined));
    return UUID.test(id) ? id : '';
};
/** The scene's encounter IF it is the host's — else the page does not hold the host's context. */
function heldEncounter(kind: string, host: Row, scene: ConditionScene): Row | null {
    const id = hostEncounterId(host);
    if (id === '') return null;
    const enc = scene.encounter;
    if (!enc || U(scalar(encounterField(enc, 'encounter_id'))) !== id) throw new ConditionContextUnknown(kind, 'encounter-not-held');
    return enc;
}
/** PHP's isset() over both arrays (a null one is absent — Codex N4e). */
const fullGeometry = (enc: Row): boolean => enc.condition_light_sources != null
    || enc.encounter_creatures_arr != null && enc.encounter_inventories_arr != null;
const conditionSources = (enc: Row): ReturnType<typeof collectLightAndDarkSources> =>
    enc.condition_light_sources as ReturnType<typeof collectLightAndDarkSources> | undefined ?? collectLightAndDarkSources(enc);
/** The host's physical position (x/y ground plane, z elevation). */
function hostPoint(kind: string, host: Row): { x: number; y: number; z: number } {
    const c = combatCenter(host);
    if (!c) throw new ConditionContextUnknown(kind, 'position-not-held');
    return c;
}
/** A campaign the page does not hold is unknown only where PHP would have found one (the encounter's master parent is a campaign —
 *  ft_effective_encounter_light / ft_campaign_context); with none PHP's answer is null (the gate is skipped). */
/** A campaign row the page actually holds (Codex N4e: PHP's is_array($campaign_arr) && !empty() — an empty {} / [] is no campaign). */
const heldCampaign = (c: unknown): boolean => c !== null && typeof c === 'object' && Object.keys(c as object).length > 0;
function campaignOrUnknown(kind: string, enc: Row, scene: ConditionScene): unknown {
    if (heldCampaign(scene.campaign)) return scene.campaign;
    if (String(scalar(encounterField(enc, 'encounter_master_parent_type')) ?? '') === 'campaign') throw new ConditionContextUnknown(kind, 'campaign-not-held');
    return null;
}
const campaignField = (campaign: unknown, key: string): unknown => {
    if (!isRow(campaign)) return undefined;
    const bucket = isRow(campaign.campaigns) ? campaign.campaigns : null;
    return bucket && key in bucket ? bucket[key] : campaign[key];
};

/** Borrow the held physical geometry; an absent shape retains the unshaped encounter contract. */
function heldShape(kind: string, enc: Row): PerceptionShape {
    let shape = encounterField<unknown>(enc, 'encounter_shape');
    if (shape == null || shape === '') return { boxes: [], openings: [] };
    if (typeof shape === 'string') {
        try { shape = JSON.parse(shape); } catch { throw new ConditionContextUnknown(kind, 'shape-not-held'); }
    }
    if (!isRow(shape) || !Array.isArray(shape.boxes)) throw new ConditionContextUnknown(kind, 'shape-not-held');
    return shape as unknown as PerceptionShape;
}

/** The physical light at the host: ambient room/daylight plus admitted light and darkness sources. */
export function effectiveLightAt(host: Row, scene: ConditionScene): string | null {
    const enc = heldEncounter('effective-light', host, scene);
    if (!enc || !fullGeometry(enc)) return null; // no / lean context — PHP skips the gate
    let base = effectiveEncounterLightId(enc, heldCampaign(scene.campaign) ? scene.campaign : null);
    // an encounter that names no override reads the campaign's light — a campaign the page does not hold is unknown, not "no light"
    if ((typeof base !== 'string' || base === '') && !heldCampaign(scene.campaign))
        base = effectiveEncounterLightId(enc, campaignOrUnknown('effective-light', enc, scene));
    if (typeof base !== 'string' || base === '') return null; // unknown base — PHP skips the gate
    const { lightSources, darkSources } = conditionSources(enc);
    const at = hostPoint('effective-light', host);
    const shape = heldShape('effective-light', enc);
    return U(lightAt(lightContext(enc, shape, { effective: U(base) }), shape, at, boxAt(shape, at), lightSources, darkSources));
}

/** No inside/underground/underwater flag or enclosing physical interior at this position. */
export function isOutdoors(host: Row, scene: ConditionScene): boolean | null {
    const enc = heldEncounter('outdoors', host, scene);
    if (!enc) return null;
    const flag = (key: string): boolean => Number(scalar(encounterField(enc, key) ?? scene.value(enc, key) ?? 0)) === 1;
    if (flag('encounter_inside') || flag('location_inside')) return false;
    // PHP compares the stored value (a shell's item_id, else [0]) with === — no case folding (Codex N4e)
    const raw = scalar(encounterField(enc, 'encounter_environment'));
    const env = isRow(raw) ? (isRow(raw.items) ? raw.items.item_id : undefined) : raw;
    if (env === ENVIRONMENT_UNDERGROUND || env === ENVIRONMENT_UNDERWATER) return false;
    const shape = heldShape('outdoors', enc);
    const box = shape.boxes.length ? boxAt(shape, hostPoint('outdoors', host)) : null;
    return !box || boxEnvironment(box, shape) !== 'interior';
}

/** creature_is_in_direct_sunlight(): magical darkness blots it out; a sunlight-flagged source's bright radius counts anywhere; else the
 *  outdoor daytime sun. */
export function inDirectSunlight(host: Row, scene: ConditionScene): boolean | null {
    const enc = heldEncounter('sunlight', host, scene);
    if (!enc) return null;
    const eff = effectiveLightAt(host, scene);
    if (eff === null) return null;
    if (eff === U(PERCEPTION_LIGHT.magicalDarkness)) return false;
    if (fullGeometry(enc)) {
        const at = hostPoint('sunlight', host);
        const shape = heldShape('sunlight', enc);
        for (const src of admittedObscuredSources(shape, at, conditionSources(enc).lightSources)) {
            if (!phpJsonTruthy(src.sunlight_light)) continue;
            const r = Math.abs(Number(src.radius) || 0);
            if (r > 0 && Math.hypot(at.x - src.x, at.y - src.z,
                src.elevation == null ? 0 : at.z - src.elevation) <= r) return true;
        }
    }
    const outdoors = isOutdoors(host, scene);
    if (outdoors === null) return null;
    if (outdoors) {
        const campaign = campaignOrUnknown('sunlight', enc, scene);
        if (campaign === null) return null; // ft_campaign_context() found none — PHP skips the gate
        // layer_value($campaign_arr, 'campaign_sun_state') === SunState day — the stored string, no case folding
        const sunRaw = scalar(campaignField(campaign, 'campaign_sun_state'));
        if ((isRow(sunRaw) ? (isRow(sunRaw.items) ? sunRaw.items.item_id : undefined) : sunRaw) === SUN_STATE_DAY) return true;
    }
    return false;
}

/** ft_host_has_controlled_summon_within_ft(): a living, present, non-private creature this host controls within `feet` (PHP's centre
 *  metric: calculate_range_from_caster_to_target without the target rim). A sizeless host measures nothing → false. */
export function controlledSummonWithin(host: Row, feet: number, scene: ConditionScene): boolean | null {
    // The creature entry marks a standalone sheet explicitly. A saved encounter
    // header on that sheet supplies no scene in which a summon could be in range.
    if (scene.encounter?.summon_range_scope === 'sheet') return false;
    const hostId = U(scalar(isRow(host.encounter_creatures) ? host.encounter_creatures.encounter_creature_id : undefined));
    if (!UUID.test(hostId)) return null;
    const enc = heldEncounter('summon-within', host, scene);
    if (!enc) throw new ConditionContextUnknown('summon-within', 'no-encounter'); // PHP then reads the controlled rows from SQL
    const scoped = Array.isArray(enc.controlled_summons);
    const roster = list(scoped ? enc.controlled_summons : enc.encounter_creatures_arr).filter(isRow);
    if (!scoped && !roster.some(r => U(scalar(isRow(r.encounter_creatures) ? r.encounter_creatures.encounter_creature_id : undefined)) === hostId))
        throw new ConditionContextUnknown('summon-within', 'host-not-in-roster');
    const has = (row: Row, key: string): boolean => scene.value(row, key) != null || scene.entries(row, key).length > 0;
    const sizeRead = { value: (row: Row, key: string) => {
        const value = scene.value(row, key);
        return key === 'size' ? readItemUuidSlot(value) || sizeIdOf(row) || value : value;
    },
        has, entries: scene.entries, inventoryField: undefined } as unknown as Parameters<typeof combatSizeFt>[1];
    if (has(host, 'swarm_width_lanes')) throw new ConditionContextUnknown('summon-within', 'swarm-geometry');
    const hostSize = combatSizeFt(host, sizeRead);
    const limit = Math.max(0, Number.isFinite(feet) ? feet : 0);
    for (const cand of roster) {
        const ec = isRow(cand.encounter_creatures) ? cand.encounter_creatures : {};
        const id = U(scalar(ec.encounter_creature_id));
        if (!UUID.test(id) || id === hostId || U(scalar(ec.controller_encounter_creature_id)) !== hostId) continue;
        const candidateEncounter = U(encounterCreatureField(ec, 'encounter_id'));
        if (candidateEncounter && candidateEncounter !== hostEncounterId(host)) continue;
        if (Number(scalar(ec.encounter_creature_dead) ?? 0) === 1 || Number(scalar(ec.encounter_creature_deleted) ?? 0) === 1
            || Number(scalar(ec.encounter_creature_private) ?? 0) === 1) continue;
        if (hostSize === null) continue; // calculate_range_from_caster_to_target(): a sizeless caster measures null → skipped
        if (has(cand, 'swarm_width_lanes')) throw new ConditionContextUnknown('summon-within', 'swarm-geometry');
        const from = combatCenter(host), to = combatCenter(cand);
        if (!from || !to) throw new ConditionContextUnknown('summon-within', 'position-not-held');
        // This condition measures size-credited centers. The shared v3 reach helper instead takes the target's full rim.
        const targetSize = combatSizeFt(cand, sizeRead);
        if (scoped && targetSize === null) throw new ConditionContextUnknown('summon-within', 'size-not-held');
        const d = Math.max(0, Math.hypot(to.x - from.x, to.y - from.y,
            combatVerticalSeparationFt(to.z - from.z, hostSize, targetSize)) - combatSizeCreditFt(hostSize) - combatSizeCreditFt(targetSize));
        if (d <= limit) return true;
    }
    return false;
}

/** Conditions and cast admission share catalog matching; the static compiler supplies its selected worn source. */
export function requiredItemsEquipped(host: Row, ability: Row | null, scene: ConditionScene): boolean {
    try { return unmetEquippedItemRequirements(host, ability, scene).length === 0; }
    catch (error) {
        if (error instanceof EquippedItemRequirementsUnknown) throw new ConditionContextUnknown('equipped-items', error.site);
        throw error;
    }
}

/** The host's own entry of that ability (any ability bucket): current charges
 *  equal max / 2; without a compiled current, use the held raw charge row (default round(max) only for a known absent row).
 *  With no bucket entry, resolve the definition's ability_charges against the host before reading its saved pool. */
export function chargeAtMedian(host: Row, abilityId: string, scene: ConditionScene): boolean {
    const want = U(abilityId);
    const ecId = U(encounterCreatureField(host.encounter_creatures, 'encounter_creature_id'));
    const queried = (max: number): number => {
        if (!UUID.test(ecId)) return Number.NaN;
        const n = scene.charges?.(ecId, want);
        if (n === undefined) throw new ConditionContextUnknown('median-charge', 'charges-not-held');
        return n === null ? Math.round(max) : n;
    };
    const entryId = (e: unknown): string => U(typeof e === 'string' ? e : isRow(e) ? (e.ability_id ?? (typeof e.item_arr === 'string' ? e.item_arr : undefined)
        ?? (isRow(e.item_arr) ? (isRow(e.item_arr.items) ? e.item_arr.items.item_id : undefined) : e.item_id) ?? readLayerListEntryUuid(e)) : '');
    const entryDef = (e: Row): Row | null => isRow(e.item_arr) && isRow(e.item_arr.item_layer_types) ? e.item_arr : scene.definition?.(want) ?? null;
    for (const type of scene.abilityTypes) for (const e of scene.entries(host, type)) {
        if (!isRow(e) || entryId(e) !== want) continue;
        const charges = isRow(e.charges) ? e.charges : null;
        let max: unknown = charges && 'max' in charges ? charges.max : 'max_ability_charges' in e ? e.max_ability_charges : undefined;
        const current: unknown = charges && 'current' in charges ? charges.current : 'ability_charges' in e ? e.ability_charges : undefined;
        if (max === undefined) {
            const def = entryDef(e);
            if (!def) throw new ConditionContextUnknown('median-charge', 'definition-not-held');
            const fallback = scene.value(def, 'ability_charges');
            max = fallback ?? null;
        }
        // is_numeric($max_raw) && (float) $max_raw > 0; then (float) $current_raw == $max / 2 (PHP's float casts — Codex N4e)
        const maxRaw = scalar(max);
        if (!isPhpNumeric(maxRaw) || phpJsonFloat(maxRaw) <= 0) continue;
        const maxN = phpJsonFloat(maxRaw);
        const cur = scalar(current);
        if (cur !== null && cur !== undefined && cur !== 'NA') return phpJsonFloat(cur) === maxN / 2;
        if (!UUID.test(ecId)) return false;
        return queried(maxN) === maxN / 2;
    }
    const def = scene.definition?.(want) ?? null;
    if (!def) throw new ConditionContextUnknown('median-charge', 'definition-not-held');
    const raw = scalar(scene.value(def, 'ability_charges'));
    if (!phpJsonTruthy(raw)) return false;
    const resolved = resolveFormulaVariables(raw, host, null, contract.rollup, {
        layerValue: (row, key) => scene.value(isRow(row) ? row : null, key),
        layerEntries: (row, key) => scene.entries(isRow(row) ? row : null, key), itemField: readItemField, computedValue,
    });
    if (!isPhpNumeric(resolved)) throw new ConditionContextUnknown('median-charge', 'charges-formula');
    const max = phpJsonFloat(resolved);
    if (max <= 0 || !UUID.test(ecId)) return false;
    return queried(max) === max / 2;
}

/** The scene-backed answers, by the evaluator's AbilityConditionSource kind. `present-light` keeps encounter_light_types_present (the
 *  compile overrides it for its own light-authoring claims). Unhandled kinds throw (unknown). */
export function answerConditionContext(kind: string, args: readonly unknown[], scene: ConditionScene | null): unknown {
    const host = isRow(args[0]) ? args[0] : null;
    if (!host) throw new ConditionContextUnknown(kind, 'no-host');
    if (!scene) throw new ConditionContextUnknown(kind, 'no-scene');
    switch (kind) {
        case 'draw-sheathed': {
            if (!scene.equipmentSlots) throw new ConditionContextUnknown(kind, 'equipment-slot-metadata');
            const inventory = scene.entries(host, 'inventory').filter(isRow);
            const id = readItemUuidSlot(args[1]);
            const row = inventory.find(row => readInventoryField(row, 'inventory_id') === id);
            if (!row) return false;
            const resolveInventoryIdentity = createInventoryTemplateIdentityResolver(id => {
                const def = scene.definition?.(U(id));
                if (!def) throw new ConditionContextUnknown(kind, 'definition-not-held');
                return def;
            });
            if (readItemField(row, 'item_type', { resolveInventoryIdentity }) !== 'weapon'
                || phpJsonInt(readInventoryField(row, 'inventory_private')) === 2) return false;
            const equipped = readItemUuidSlot(readInventoryField(row, 'equipped'));
            if (Object.values(scene.equipmentSlots).includes(equipped)) return false;
            // Native resolve_best_equipment_slot returns the preferred slot even
            // with occupied hands; a blank preference resolves to off_hand.
            return phpJsonTruthy(scene.value(row, 'equipment_slot')) || phpJsonTruthy(scene.equipmentSlots.off_hand);
        }
        case 'present-light': {
            if (hostEncounterId(host) === '') return null;
            const enc = heldEncounter(kind, host, scene);
            const present = encounterLightTypesPresent(enc, scene.campaign, encounterCreatureField(host.encounter_creatures, 'encounter_light'),
                enc ? conditionSources(enc) : undefined);
            return present === null ? null : present.map(U);
        }
        case 'effective-light': return effectiveLightAt(host, scene);
        case 'outdoors': return isOutdoors(host, scene);
        case 'sunlight': return inDirectSunlight(host, scene);
        case 'summon-within': return controlledSummonWithin(host, Number(args[1]), scene);
        case 'equipped-items': return requiredItemsEquipped(host, isRow(args[1]) ? args[1] : null, scene);
        case 'median-charge': return chargeAtMedian(host, String(args[1] ?? ''), scene);
        default: throw new ConditionContextUnknown(kind, 'not-modelled');
    }
}
